import {
  TechnicianAccountStatus,
  TechnicianAssignedServiceRequestRow,
  TechnicianAssignedServiceRequestSearchParam,
  TechnicianAssignedServiceRequestSearchResponse,
  TechnicianCompetencyRow,
  TechnicianFormListItemDto,
  TechnicianFormListQuery,
  TechnicianFormPagedResponse,
  TechnicianFormRow,
  TechnicianFormSearchField,
  TechnicianFormSearchParam,
  TechnicianFormSearchResponse,
  TechnicianHeldCompetencyDto,
  TechnicianListItem,
  TechnicianListPagedResponse,
  TechnicianListQuery,
  TechnicianProfile,
  TechnicianProfileResponse,
  TechnicianRosterRow,
  TechnicianRosterSearchParam,
  TechnicianRosterSearchResponse,
  TechnicianScheduleItemDto,
  TechnicianScheduleListQuery,
  TechnicianSchedulePagedResponse,
} from '@shared/models/technician/technician.model';
import { formatTechnicianRegionLabels } from '@shared/utilities/technician-roster-render.util';

const ROSTER_SORT_FIELD_MAP: Record<string, string> = {
  technicianid: 'technicianId',
  technicianname: 'displayName',
  techniciancode: 'technicianId',
  region: 'regionId',
  lastupdate: 'lastUpdated',
  updatedby: 'updatedBy',
  status: 'status',
};

export function mapAccountStatusFromWire(status: string): TechnicianAccountStatus {
  return status?.toUpperCase() === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE';
}

export function mapAccountStatusToWire(status: TechnicianAccountStatus): string {
  return status === 'INACTIVE' ? 'Inactive' : 'Active';
}

export function mapRosterSearchParamToListQuery(
  params: TechnicianRosterSearchParam,
): TechnicianListQuery {
  const filter = params.filter;
  const sort = parseRosterSort(params.sort);
  let search = filter?.searchParam?.trim() ?? '';

  if (filter?.searchField === 'TECHNICIAN_ID' && search) {
    search = search;
  }

  return {
    clientId: params.clientId,
    ...(search && { search }),
    ...(filter?.regionIdFilter != null && { regionId: filter.regionIdFilter }),
    ...(filter?.statusFilter && { status: mapAccountStatusToWire(filter.statusFilter) }),
    ...(filter?.isDispatchable != null && { isDispatchable: filter.isDispatchable }),
    page: params.pageNumber,
    pageSize: params.pageSize,
    ...(sort.sortBy && { sortBy: sort.sortBy }),
    ...(sort.sortDirection && { sortDirection: sort.sortDirection }),
  };
}

export function mapListPagedResponseToRosterSearch(
  response: TechnicianListPagedResponse,
): TechnicianRosterSearchResponse {
  return {
    recordsTotal: response.totalCount,
    recordsFiltered: response.totalCount,
    data: response.items.map(mapListItemToRosterRow),
  };
}

export function mapListItemToRosterRow(item: TechnicianListItem): TechnicianRosterRow {
  return {
    technicianId: item.technicianId,
    userId: item.userId,
    displayName: item.displayName,
    regions: item.regions ?? [],
    regionLabel: formatTechnicianRegionLabels(item.regions),
    lastUpdated: item.lastUpdated ?? '',
    updatedBy: item.updatedBy ?? '',
    status: mapAccountStatusFromWire(item.status),
    isDispatchable: item.isDispatchable,
    certifications: item.certifications ?? [],
    serviceRequest: item.serviceRequest,
  };
}

export function mapProfileResponseToProfile(
  response: TechnicianProfileResponse,
): TechnicianProfile {
  return {
    technicianId: response.technicianId,
    userId: response.userId,
    displayName: response.displayName,
    status: mapAccountStatusFromWire(response.status),
    regions: response.regions ?? [],
    lastUpdated: response.lastUpdated ?? '',
    updatedBy: response.updatedBy,
    isDispatchable: response.isDispatchable,
    competencies: response.competencies ?? [],
    certifications: response.certifications ?? [],
  };
}

export function mapHeldCompetencyToRow(
  holding: TechnicianHeldCompetencyDto,
): TechnicianCompetencyRow {
  return {
    technicianCompetencyId: holding.technicianCompetencyId,
    competencyCode: holding.competencyCode,
    skill: holding.competencyName,
    remarks: holding.remark,
    dateAdded: holding.dateAdded,
    updatedBy: holding.addedBy,
    lastUpdated: holding.dateAdded,
    competencyStatus: holding.competencyStatus,
  };
}

const SCHEDULE_CLIENT_FILTER_MAX_PAGE_SIZE = 100;

const FORM_SEARCH_FIELD_MAP: Record<TechnicianFormSearchField, string> = {
  FORM_ID: 'formCode',
  FORM_NAME: 'formName',
  INSPECTION_NO: 'inspectionNo',
};

const FORM_SORT_FIELD_MAP: Record<string, string> = {
  formid: 'formId',
  formcode: 'formCode',
  formname: 'formName',
  inspectionno: 'inspectionNo',
  lastupdated: 'lastUpdate',
  lastupdate: 'lastUpdate',
  status: 'status',
};

export function mapFormSearchParamToListQuery(
  params: TechnicianFormSearchParam,
): TechnicianFormListQuery {
  const filter = params.filter;
  const sort = parseFormSort(params.sort);
  const search = filter?.searchParam?.trim() ?? '';
  const searchField = filter?.searchField ?? 'FORM_ID';

  return {
    clientId: params.clientId,
    technicianId: params.technicianId,
    page: params.pageNumber,
    pageSize: params.pageSize,
    ...(search && {
      search,
      searchParam: FORM_SEARCH_FIELD_MAP[searchField],
    }),
    ...(filter?.status && { status: filter.status }),
    ...(sort.sortBy && { sortBy: sort.sortBy }),
    ...(sort.sortDirection && { sortDirection: sort.sortDirection }),
  };
}

export function mapFormListItemToRow(item: TechnicianFormListItemDto): TechnicianFormRow {
  return {
    formsId: item.placementId,
    formId: item.formId,
    formCode: item.formCode,
    formName: item.formName,
    inspectionNumber: item.inspectionNo,
    serviceRequestId: item.serviceRequestId,
    serviceTicket: item.serviceTicket,
    accountName: '',
    accountNumber: '',
    locationName: '',
    locationAddress: '',
    region: '',
    status: item.status,
    lastUpdated: item.lastUpdate,
  };
}

export function mapFormPagedResponseToFormSearch(
  response: TechnicianFormPagedResponse,
): TechnicianFormSearchResponse {
  return {
    recordsTotal: response.totalCount,
    recordsFiltered: response.totalCount,
    data: (response.data ?? []).map(mapFormListItemToRow),
  };
}

function parseFormSort(sort?: string): { sortBy?: string; sortDirection?: string } {
  if (!sort) {
    return { sortBy: 'lastUpdate', sortDirection: 'desc' };
  }

  const [rawField, rawDirection] = sort.split(':');
  const sortBy = FORM_SORT_FIELD_MAP[rawField?.toLowerCase() ?? ''] ?? rawField;
  const sortDirection = rawDirection?.toLowerCase() === 'asc' ? 'asc' : 'desc';

  return { sortBy, sortDirection };
}

const ASSIGNED_ROW_SORT_FIELD_MAP: Record<string, keyof TechnicianAssignedServiceRequestRow> = {
  serviceticket: 'serviceTicket',
  servicetype: 'serviceType',
  requestdate: 'requestDate',
  servicedate: 'serviceDate',
  accountnumber: 'accountNumber',
  accountname: 'accountName',
  location: 'location',
  region: 'region',
  sladays: 'slaDays',
  status: 'status',
};

export function mapAssignedSearchParamToScheduleQuery(
  params: TechnicianAssignedServiceRequestSearchParam,
  options?: { forceClientFilterFetch?: boolean },
): TechnicianScheduleListQuery {
  const needsClientFilter = options?.forceClientFilterFetch
    ?? Boolean(params.filter?.searchParam?.trim() || params.filter?.status);

  return {
    clientId: params.clientId,
    technicianId: params.technicianId,
    page: needsClientFilter ? 1 : params.pageNumber,
    pageSize: needsClientFilter ? SCHEDULE_CLIENT_FILTER_MAX_PAGE_SIZE : params.pageSize,
  };
}

export function mapScheduleItemToAssignedRow(
  item: TechnicianScheduleItemDto,
): TechnicianAssignedServiceRequestRow {
  return {
    assignmentId: item.assignmentId,
    serviceRequestId: item.serviceRequestId,
    serviceTicket: item.serviceTicket,
    serviceType: item.serviceRequestTypeName,
    requestDate: item.requestDate,
    serviceDate: item.scheduleDate,
    accountNumber: item.accountNo,
    accountName: item.accountName,
    customerId: item.accountId,
    location: item.location,
    region: item.regionName,
    slaDays: item.slaDays ?? undefined,
    status: item.status,
    scheduledStart: item.scheduledStart,
    durationMinutes: item.durationMinutes,
    arrivalTime: item.arrivalTime,
    departureTime: item.departureTime,
    durationOnSiteMinutes: item.durationOnSiteMinutes,
    hasWorkPackageGaps: item.hasWorkPackageGaps,
  };
}

export function mapSchedulePagedResponseToAssignedSearch(
  response: TechnicianSchedulePagedResponse,
  params: TechnicianAssignedServiceRequestSearchParam,
  clientFiltered: boolean,
): TechnicianAssignedServiceRequestSearchResponse {
  let rows = (response.data ?? []).map(mapScheduleItemToAssignedRow);
  const search = params.filter?.searchParam?.trim().toLowerCase();
  const status = params.filter?.status?.trim();

  if (search) {
    rows = rows.filter((row) => row.serviceTicket.toLowerCase().includes(search));
  }

  if (status) {
    rows = rows.filter((row) => normalizeAssignedStatus(row.status) === normalizeAssignedStatus(status));
  }

  rows = sortAssignedRows(rows, params.sort);

  if (clientFiltered) {
    const start = (params.pageNumber - 1) * params.pageSize;
    const pageRows = rows.slice(start, start + params.pageSize);

    return {
      recordsTotal: response.totalCount,
      recordsFiltered: rows.length,
      data: pageRows,
    };
  }

  return {
    recordsTotal: response.totalCount,
    recordsFiltered: response.totalCount,
    data: rows,
  };
}

function normalizeAssignedStatus(status: string): string {
  return status.replace(/[\s_-]+/g, '').toUpperCase();
}

function sortAssignedRows(
  rows: TechnicianAssignedServiceRequestRow[],
  sort?: string,
): TechnicianAssignedServiceRequestRow[] {
  const [rawField, rawDirection] = (sort ?? 'servicedate:desc').split(':');
  const field = ASSIGNED_ROW_SORT_FIELD_MAP[rawField?.toLowerCase() ?? ''] ?? 'serviceDate';
  const desc = rawDirection?.toLowerCase() !== 'asc';

  return [...rows].sort((left, right) => {
    const leftValue = String(left[field] ?? '');
    const rightValue = String(right[field] ?? '');
    if (leftValue === rightValue) {
      return 0;
    }

    const comparison = leftValue.localeCompare(rightValue, undefined, { numeric: true });
    return desc ? -comparison : comparison;
  });
}

function parseRosterSort(sort?: string): { sortBy?: string; sortDirection?: string } {
  if (!sort) {
    return {};
  }

  const [rawField, rawDirection] = sort.split(':');
  const mappedField = ROSTER_SORT_FIELD_MAP[rawField?.toLowerCase() ?? ''] ?? rawField;
  const sortDirection = rawDirection?.toLowerCase() === 'asc' ? 'asc' : 'desc';

  return {
    sortBy: mappedField,
    sortDirection,
  };
}
